<template>
  <el-upload
    v-model:file-list="fileList"
    class="oss-upload"
    :class="{ 'hide-upload-btn': isLimitReached }"
    :action="isLimitReached ? '' : oss?.host"
    :data="uploadData"
    :before-upload="handleBeforeUpload"
    :on-success="handleSuccess"
    :on-remove="handleRemove"
    :multiple="multiple"
    :limit="limit"
    list-type="picture-card"
    :disabled="isLimitReached"
  >
    <!-- 这里不需要 v-if，直接由外层 CSS 控制显隐 -->
    <div class="upload-plus">
      <el-icon><Plus /></el-icon>
    </div>
  </el-upload>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { Plus } from '@element-plus/icons-vue'
import { useCurrentAppInstance } from '@zto/zpage'

const app = useCurrentAppInstance()

const props = withDefaults(
  defineProps<{
    multiple?: boolean
    limit?: number
  }>(),
  {
    multiple: false,
    limit: 1
  }
)

const emit = defineEmits<{
  (e: 'success', url: string): void
}>()

const oss = ref<any>(null)
const uploadData = ref<any>({})
const fileList = ref<any[]>([])

// 只要 fileList 长度变化，这里就会自动更新
const isLimitReached = computed(() => {
  return fileList.value.length >= props.limit
})

async function handleBeforeUpload(file: File) {
  if (isLimitReached.value) return false

  const res = await app.request({
    api: '/base/oss/get_post_signature_for_oss_upload'
  })

  oss.value = res

  const suffix = file.name.slice(file.name.lastIndexOf('.'))
  const key = `${oss.value.dir}/${Date.now()}_${Math.random()
    .toString(36)
    .slice(2)}${suffix}`

  uploadData.value = {
    key,
    policy: oss.value.policy,
    'x-oss-signature': oss.value.signature,
    'x-oss-signature-version': oss.value.version,
    'x-oss-credential': oss.value.x_oss_credential,
    'x-oss-date': oss.value.x_oss_date,
    'x-oss-security-token': oss.value.security_token,
    success_action_status: '200'
  }

  return true
}

// ⭐ 重点修改：handleSuccess 参数和逻辑
function handleSuccess(response: any, uploadFile: any) {
  // 1. 拼接 URL
  const url = `${oss.value.host}/${uploadData.value.key}`

  // 2. 将 URL 赋值给当前文件对象，以便预览
  uploadFile.url = url

  // 3. ❌ 绝对不要写 fileList.value.push(file)
  // 因为 v-model 已经在选图时自动加进去了，再次 push 会导致错误或重复

  // 4. 抛出事件
  emit('success', url)
}

function handleRemove(file: any) {
  // 处理移除逻辑
  fileList.value = fileList.value.filter(
    item => item.uid !== file.uid
  )
}
</script>

<style scoped>
.oss-upload {
  /* 这里的 flex 布局可能会干扰 el-upload 内部的默认布局，建议移除或谨慎使用 */
  /* display: flex; flex-wrap: wrap; gap: 8px; */
}

/* 调整图片卡片的大小 */
:deep(.el-upload--picture-card),
:deep(.el-upload-list__item) {
  width: 96px;
  height: 96px;
}

.upload-plus {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

:deep(.el-upload-list__item-thumbnail) {
  object-fit: cover;
}

/* 核心代码：当有这个类名时，隐藏上传按钮容器 */
.hide-upload-btn :deep(.el-upload--picture-card) {
  display: none;
}
</style>
